import axios from "axios";
import { encryptPayload, decryptPayload } from "./crypto";
// import { logout } from "../redux/authSlice";
import { logout, setAccessToken } from "../redux/authSlice";
import store from "../redux/store";
import { logger } from "./logger";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 100000,
  headers: {
    "Cache-Control": "no-store, no-cache, must-revalidate, proxy-revalidate",
    Pragma: "no-cache",
    Expires: "0",
  },
});

// Encrypt request data
axiosInstance.interceptors.request.use(
  (config) => {
    // const token = sessionStorage.getItem("accessToken");

    const state = store.getState();
    const token = state?.auth?.token || sessionStorage.getItem("accessToken");

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    if (config.data && !(config.data instanceof FormData)) {
      config.data = { payload: encryptPayload(config.data) };
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Decrypt response data and preserve token/refreshToken
axiosInstance.interceptors.response.use(
  (response) => {
    if (response.data && response.data.payload) {
      try {
        const decrypted = decryptPayload(response.data.payload);
        if (Array.isArray(decrypted)) {
          // If decrypted is an array, attach tokens as properties (if needed)
          response.data = decrypted;
          if (response.data.token || response.data.refreshToken) {
            // Optionally, you can return an object like { data: [...], token, refreshToken }
            response.data = {
              data: decrypted,
              ...(response.data.token && { token: response.data.token }),
              ...(response.data.refreshToken && {
                refreshToken: response.data.refreshToken,
              }),
            };
          }
        } else {
          // If decrypted is an object, merge as before
          response.data = {
            ...decrypted,
            ...(response.data.token && { token: response.data.token }),
            ...(response.data.refreshToken && {
              refreshToken: response.data.refreshToken,
            }),
          };
        }
      } catch (e) {
        response.data = { error: "Decryption failed" };
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.data?.payload) {
      try {
        const decrypted = decryptPayload(error.response.data.payload);
        error.response.data = decrypted;
      } catch (e) {
        error.response.data = { message: "Error decrypting response." };
        console.error("Failed to decrypt error payload:", e.message);
      }
    }
    if (
      error.response &&
      error.response.status === 401 &&
      error.response.data.message === "Token expired" &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      try {
        const refreshToken = sessionStorage.getItem("refreshToken");
        if (!refreshToken) throw new Error("No refresh token available");
        const url = `${process.env.REACT_APP_BASE_URL}/api/auth/refresh`;
        const { data } = await axiosInstance.post(url, { refreshToken });
        // const newAccessToken = data.accessToken;
        // sessionStorage.setItem("token", newAccessToken);
        const newAccessToken = data.accessToken;
        sessionStorage.setItem("accessToken", newAccessToken);

        store.dispatch(setAccessToken(newAccessToken));
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        const authState = store.getState()?.auth;
        logger.warn("Session expired, redirecting to login", {
          username: authState?.user?.username,
          role: authState?.role,
          reason: refreshError?.message || "Refresh token invalid or missing",
        });
        store.dispatch(logout());
        sessionStorage.clear();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    // Centralized failure log — every API call goes through this interceptor,
    // so this is the one place that can catch network drops/timeouts (no
    // error.response at all) and non-401 HTTP failures without having to
    // instrument every thunk's catch block individually. 4xx is logged as
    // warn (expected/handled cases like validation errors) so real outages
    // (5xx, no response) stand out as error.
    const status = error.response?.status;
    const authState = store.getState()?.auth;
    const level = status && status < 500 ? "warn" : "error";
    logger[level]("API request failed", {
      username: authState?.user?.username,
      role: authState?.role,
      url: originalRequest?.url,
      method: originalRequest?.method,
      status: status || "no response (network error)",
      message: error.response?.data?.message || error.message,
    });

    return Promise.reject(error);
  },
);

export default axiosInstance;
